import * as THREE from "three";
import { CAMERA, EXPLORED } from "../config";

export class Explored {
  readonly texture: THREE.DataTexture;

  private readonly data: Uint8Array;
  private readonly cells = EXPLORED.cells;
  private readonly axisX: number;
  private readonly axisZ: number;
  private readonly acrossX: number;
  private readonly acrossZ: number;
  private readonly stretch: number;
  private readonly world = EXPLORED.worldSize;
  private lastX = Number.POSITIVE_INFINITY;
  private lastZ = Number.POSITIVE_INFINITY;

  constructor() {
    // a circle stamped on the ground projects to an ellipse, so the footprint is
    // stretched along the depth axis until it reads as a circle on screen
    const azimuth = THREE.MathUtils.degToRad(CAMERA.azimuth);
    const elevation = THREE.MathUtils.degToRad(CAMERA.elevation);
    this.axisX = Math.cos(azimuth);
    this.axisZ = -Math.sin(azimuth);
    this.acrossX = Math.sin(azimuth);
    this.acrossZ = Math.cos(azimuth);
    this.stretch = 1 / Math.max(0.2, Math.sin(elevation));

    const total = this.cells * this.cells;
    this.data = new Uint8Array(total);
    this.texture = new THREE.DataTexture(this.data, this.cells, this.cells, THREE.RedFormat);
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.wrapS = THREE.ClampToEdgeWrapping;
    this.texture.wrapT = THREE.ClampToEdgeWrapping;
    this.texture.needsUpdate = true;
  }

  reveal(
    x: number,
    z: number,
    radius: number = EXPLORED.radius,
    force = false,
  ): void {
    // the step guard stops a standing player re-stamping every frame. A growing
    // radius is the exception: it has to redraw continuously to read as a swell.
    if (!force && Math.hypot(x - this.lastX, z - this.lastZ) < EXPLORED.step) return;
    this.lastX = x;
    this.lastZ = z;
    this.stamp(x, z, radius);
  }

  private stamp(x: number, z: number, radius: number): void {
    const cells = this.cells;
    const centreX = ((x + this.world * 0.5) / this.world) * cells;
    const centreZ = ((z + this.world * 0.5) / this.world) * cells;
    const toCells = cells / this.world;
    const near = radius * toCells;
    const far = near * this.stretch;
    const bound = Math.max(near, far);

    const fromX = Math.max(0, Math.floor(centreX - bound));
    const toX = Math.min(cells - 1, Math.ceil(centreX + bound));
    const fromZ = Math.max(0, Math.floor(centreZ - bound));
    const toZ = Math.min(cells - 1, Math.ceil(centreZ + bound));

    for (let gz = fromZ; gz <= toZ; gz++) {
      for (let gx = fromX; gx <= toX; gx++) {
        const dx = gx + 0.5 - centreX;
        const dz = gz + 0.5 - centreZ;
        const along = (dx * this.axisX + dz * this.axisZ) / near;
        const across = (dx * this.acrossX + dz * this.acrossZ) / far;
        if (along * along + across * across <= 1) this.data[gz * cells + gx] = 255;
      }
    }

    this.texture.needsUpdate = true;
  }
}
